// chat/voiceTools.ts — THE SEAM between a live voice call and everything this app can do.
//
// runVoiceTool has the SAME signature in every Oriora app; the one line that differs is the call
// into this app's own toAction, because the fleet has five different shapes of it. That variation
// is why the shared live-session code must NEVER call toAction directly — it is exactly what
// broke the fleet chat codemod (2026-08-13).
//
// Voice runs the SAME path a typed message takes: name+args → ChatAction → executeAction. There
// is no second implementation to drift from the chat.
//
// SAFETY: the model is told to ask out loud before anything destructive, but a spoken "yes" is
// NOT a security boundary — it can mishear. Destructive verbs are re-checked HERE.
// ⚠️ `confirmed` arrives from the model as the STRING "true", not a boolean (measured against
// Gemini Live, 2026-08-13) — a strict `=== true` check silently blocks every voice deletion.
import { toAction } from "./brain";
import { executeAction } from "./actions";
import { getAccessToken } from "../supabase";

const APP_ID = "oriora_garage";

export interface VoiceToolResult {
  ok: boolean;
  message: string;
  changed?: boolean;
}

const DESTRUCTIVE = /^(delete|remove|merge|clear|reset|restore|import|wipe)|_(delete|remove|merge|restore)$/i;

const isConfirmed = (a: Record<string, unknown>): boolean =>
  a["confirmed"] === true || String(a["confirmed"] ?? "").toLowerCase() === "true" ||
  String(a["confirm"] ?? "").toLowerCase() === "yes";

/** Run ONE in-call tool. Never throws — a live call must not die because a tool failed. */
export async function runVoiceTool(name: string, args: Record<string, unknown>, customerSaid = "", sessionId = ""): Promise<VoiceToolResult> {
  try {
    // cloud_* verbs are enforced on the SERVER against the customer's own transcribed words.
    // Do NOT gate them on a model-written param here: that is the fictitious-consent pattern the
    // security review caught in chat-brain (2026-08-13), and it would also block a legitimate
    // spoken "yes" whenever the model simply omitted the flag.
    if (!name.startsWith("cloud_") && DESTRUCTIVE.test(name) && !isConfirmed(args)) {
      return { ok: false, message: "Not done yet — say clearly that you want this, and I'll do it." };
    }

    // ── the ONE app-shaped line ────────────────────────────────────────────────
    const r = await toAction({ action: name, params: args, reply: "" }, { vehicleId: null, vehicleName: null });

    const acts = [r.action].filter((a) => a && a.kind !== "none");
    if (!acts.length) {
      const first = r.action as { message?: string } | undefined;
      // Not a failure — it may be a tool only the SERVER can run (the customer's own cloud).
      if (!first?.message) return await forwardToServer(name, args, customerSaid, sessionId);
      return { ok: false, message: first.message };
    }
    const lines: string[] = [];
    let changed = false;
    for (const a of acts) {
      const res = await executeAction(a);
      lines.push(res.message);
      if (res.changed) changed = true;
    }
    // The REAL outcome, never the model's claim (APP-CHAT-AGENT-STANDARD §2.3).
    return { ok: true, message: lines.filter(Boolean).join(" "), changed };
  } catch (err) {
    return { ok: false, message: `That didn't work: ${String(err)}` };
  }
}

/** Tools this app cannot run itself go to the SERVER, which holds the customer's cloud
 *  credential. Deliberately GENERIC: every future server-side power reaches live voice through
 *  this same path with no further app release — the property the typed chat already has. */
async function forwardToServer(name: string, args: Record<string, unknown>, customerSaid: string, sessionId: string): Promise<VoiceToolResult> {
  try {
    const token = await getAccessToken();
    if (!token) return { ok: false, message: "You're signed out — sign in and I can do that." };
    const r = await fetch("https://api.orioralabs.com/v1/realtime/tool", {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
      // customer_said carries the customer's OWN transcribed words — the consent source for
      // destructive verbs. A model-written flag would be forgeable by anything it reads.
      body: JSON.stringify({ name, args, app_id: APP_ID, customer_said: customerSaid, session_id: sessionId }),
    });
    if (!r.ok) return { ok: false, message: `I couldn't do that (${r.status}).` };
    const j = (await r.json()) as { ok?: boolean; message?: string };
    return { ok: !!j.ok, message: j.message ?? "", changed: !!j.ok };
  } catch (err) {
    return { ok: false, message: `That didn't work: ${String(err)}` };
  }
}
